import {
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Delete, Edit, Add } from '@mui/icons-material';
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';

export function BankManagement({
  open,
  onClose,
  bankAccounts = [],
  dispatch,
  actions,
  me,
}) {
  const [accounts, setAccounts] = useState([]);
  const [editMode, setEditMode] = useState(true);
  const [editAccount, setEditAccount] = useState(null);
  const [loading, setLoading] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: {
      name: '',
      bankName: '',
      accountNumber: '',
      isCash: false,
      status: false,
    },
  });

  useEffect(() => {
    setAccounts(bankAccounts || []);
  }, [bankAccounts]);

  const loadBankData = async () => {
    try {
      await dispatch(actions.bankAll());
    } catch (error) {
      console.error('Error loading banks:', error);
    }
  };
  const isCash = watch('isCash');
  const handleAdd = () => {
    setEditMode(true);
    setEditAccount(null);
    reset({ name: '', bankName: '', accountNumber: '' });
  };

  const handleEdit = (account) => {
    setEditMode(true);
    setEditAccount(account);
    reset({
      name: account.name || '',
      bankName: account.bankName || '',
      accountNumber: account.accountNumber || '',
    });
  };

  const handleDelete = async (account) => {
    if (window.confirm('คุณต้องการลบบัญชีธนาคารนี้หรือไม่?')) {
      setLoading(true);
      try {
        await dispatch(actions.bankDelete(account._id));
        await loadBankData();
        alert('ลบข้อมูลสำเร็จ');
      } catch (error) {
        console.error('Error deleting bank:', error);
        alert('เกิดข้อผิดพลาดในการลบข้อมูล');
      } finally {
        setLoading(false);
      }
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const payload = {
        ...data,
      };

      if (editAccount) {
        // Update existing bank
        await dispatch(actions.bankPut(editAccount._id, payload));
        alert('แก้ไขข้อมูลสำเร็จ');
      } else {
        // Create new bank
        await dispatch(actions.bankCreate(payload));
        alert('เพิ่มข้อมูลสำเร็จ');
      }

      await loadBankData();
      setEditMode(false);
      reset();
    } catch (error) {
      console.error('Error saving bank:', error);
      alert('เกิดข้อผิดพลาดในการบันทึกข้อมูล');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    setEditAccount(null);
    reset();
  };

  const renderField = (name, label, required = true) => (
    <div className="mb-4">
      <Controller
        name={name}
        control={control}
        rules={{ required: required && 'กรุณาใส่ข้อมูล' }}
        render={({ field }) => (
          <TextField
            {...field}
            label={label}
            required={required}
            fullWidth
            size="small"
            error={!!errors[name]}
            helperText={errors[name]?.message}
          />
        )}
      />
    </div>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>จัดการข้อมูลบัญชีธนาคาร</DialogTitle>

      <DialogContent>
        {/* รายการบัญชีธนาคาร */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">รายการบัญชีธนาคาร</h3>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={handleAdd}
              size="small"
            >
              เพิ่มบัญชี
            </Button>
          </div>

          {accounts.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              ยังไม่มีข้อมูลบัญชีธนาคาร
            </div>
          ) : (
            <div className="space-y-3">
              {accounts.map((account, index) => (
                <div
                  key={account._id || index}
                  className="border rounded-lg p-4 bg-gray-50"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="text-sm font-medium text-gray-600">
                            ชื่อธนาคาร
                          </label>
                          <p className="text-base font-medium">
                            {account.name || '-'}
                          </p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">
                            ชื่อบัญชี
                          </label>
                          <p className="text-base">{account.bankName || '-'}</p>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">
                            เลขที่บัญชี
                          </label>
                          <p className="text-base font-mono">
                            {account.accountNumber || '-'}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex space-x-1 ml-4">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleEdit(account)}
                        title="แก้ไข"
                        disabled={loading}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(account)}
                        title="ลบ"
                        disabled={loading}
                      >
                        <Delete />
                      </IconButton>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ฟอร์มเพิ่ม/แก้ไข */}
        {editMode && (
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold mb-4">
              {editAccount ? 'แก้ไขบัญชีธนาคาร' : 'เพิ่มบัญชีธนาคารใหม่'}
            </h3>

            <form onSubmit={handleSubmit(onSubmit)}>
              {me?.userData?.department?.access.OTHER?.cash && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* เงินสด */}
                  <Controller
                    name="isCash"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            {...field}
                            checked={!!field.value}
                            color="primary"
                          />
                        }
                        label="เงินสด (Cash)"
                      />
                    )}
                  />
                </div>
              )}

              {isCash ? (
                <div>
                  {' '}
                  <div>{renderField('bankName', 'ชื่อบัญชี')}</div>
                </div> // ถ้าเป็นเงินสด ไม่ต้องกรอกธนาคาร
              ) : (
                <div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>{renderField('name', 'ชื่อธนาคาร')}</div>
                    <div>{renderField('bankName', 'ชื่อบัญชี')}</div>
                  </div>
                  {renderField('accountNumber', 'เลขที่บัญชี')}
                </div>
              )}
              <div className="flex space-x-2">
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="small"
                  disabled={loading}
                >
                  {loading
                    ? 'กำลังบันทึก...'
                    : editAccount
                    ? 'บันทึกการแก้ไข'
                    : 'เพิ่มบัญชี'}
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={handleCancel}
                  size="small"
                  disabled={loading}
                >
                  ยกเลิก
                </Button>
              </div>
            </form>
          </div>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={loading}>
          ปิด
        </Button>
      </DialogActions>
    </Dialog>
  );
}

BankManagement.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  bankAccounts: PropTypes.array,
  dispatch: PropTypes.func.isRequired,
  actions: PropTypes.object.isRequired,
};

export default BankManagement;
